import { Component, Input, input, OnDestroy, output, TemplateRef, ChangeDetectionStrategy } from "@angular/core";
import { SearchErrorWithResults, SearchQuery } from "common/autoCompleteSearch/search-query";
import { Subject, Subscription } from "rxjs";
import { AutoCompleteComponent } from "common/autoComplete/auto-complete.component";

export type CreateAllowedCallback = (args: unknown, args2: unknown) => boolean;

const MAX_SEARCH_TERM_LENGTH = Number.MAX_SAFE_INTEGER;
const MIN_SEARCH_TERM_LENGTH = 1;

@Component({
	selector: "stages-auto-complete-search",
	templateUrl: "auto-complete-search.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [AutoCompleteComponent],
})
export class AutoCompleteSearchComponent<T> implements OnDestroy {
	readonly backButtonActive = input<boolean>(true);

	readonly browseButtonActive = input<boolean>(false);

	readonly createDependendElementAllowedCallback = input<CreateAllowedCallback>();

	readonly dropDownClass = input<string>();

	readonly focusOnInit = input<boolean>(false);

	readonly maxSearchTermLength = input<number>(MAX_SEARCH_TERM_LENGTH);

	readonly minSearchTermLength = input<number>(MIN_SEARCH_TERM_LENGTH);

	readonly inputIconClasses = input<string[]>();

	readonly isDense = input<boolean>(false);

	// TODO: Skipped for migration because:
	//  Accessor inputs cannot be migrated as they are too complex.
	@Input() set isInSubordinatedElementsMode(isInSubordinatedElementsMode: boolean) {
		this._isInSubordinatedElementsMode = isInSubordinatedElementsMode;
		if (isInSubordinatedElementsMode || this.currentTerm.length >= this.minSearchTermLength()) {
			this.triggerOnSearchInput$.next(this.currentTerm);
		}
	}

	readonly placeholder = input<string>("");

	readonly query = input.required<SearchQuery<T>>();

	readonly searchResultSeparatorTemplate = input<TemplateRef<unknown>>();

	readonly searchResultTemplate = input<TemplateRef<unknown>>();

	readonly backButtonPressed = output<void>();

	readonly cancelled = output<void>();

	readonly closeButtonPressed = output<void>();

	readonly focusLost = output<T>();

	readonly openBrowserButtonPressed = output<void>();

	readonly submitted = output<T>();

	_isInSubordinatedElementsMode: boolean = false;

	currentTerm: string = "";

	loading: boolean = false;

	oldTerm?: string;

	results: T[] = [];
	subordinatedResults?: T[];

	searchEngineAvailable = true;

	selectedParentItem?: T;

	private subscriptions = new Subscription();

	private destroy$: Subject<boolean> = new Subject<boolean>();

	private triggerOnSearchInput$: Subject<string> = new Subject<string>();

	constructor() {
		// eslint-disable-next-line @typescript-eslint/no-misused-promises -- TODO: Remove this comment and fix warnings!
		this.subscriptions.add(this.triggerOnSearchInput$.subscribe(async (term) => this.onSearchInput(term)));
	}

	async onSearchInput(term: string): Promise<void> {
		this.currentTerm = term;
		this.loading = true;
		try {
			await this.executeSearchQuery(term);
			this.searchEngineAvailable = true;
		} catch (e: unknown) {
			this.passErrorToResults(e);
			this.searchEngineAvailable = false;
		} finally {
			this.loading = false;
		}
	}

	async executeSearchQuery(term: string): Promise<void> {
		if (this._isInSubordinatedElementsMode) {
			this.subordinatedResults = await this.query().run(term);
		} else {
			this.results = await this.query().run(term);
		}
	}

	passErrorToResults(error: unknown): void {
		if (this._isInSubordinatedElementsMode) {
			this.subordinatedResults = error instanceof SearchErrorWithResults ? error.results : [];
		} else {
			this.results = error instanceof SearchErrorWithResults ? error.results : [];
		}
	}

	back(): void {
		this.selectedParentItem = undefined;
		this.subordinatedResults = undefined;
		if (this.oldTerm) {
			this.currentTerm = this.oldTerm;
		}
		// TODO: The 'emit' function requires a mandatory void argument
		this.backButtonPressed.emit();
	}

	isCreateAllowed(item: unknown): boolean {
		const createDependendElementAllowedCallback = this.createDependendElementAllowedCallback();
		if (createDependendElementAllowedCallback && this._isInSubordinatedElementsMode) {
			return createDependendElementAllowedCallback(item, this.selectedParentItem);
		}
		return true;
	}

	itemSelected(item: T): void {
		this.selectedParentItem = item;
		this.oldTerm = this.currentTerm;
		this.currentTerm = "";
		this.submitted.emit(item);
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
		this.destroy$.next(true);
	}
}
